import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/lib/queryKeys'
import { fetchColumnWidths, upsertColumnWidth } from '@/api/columnWidths'
import { toast } from '@/stores/useToastStore'
import { getErrorMessage } from '@/utils/getErrorMessage'

export function useColumnWidths(planId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.columnWidths.byPlan(planId!),
    queryFn: () => fetchColumnWidths(planId!),
    enabled: !!planId,
    staleTime: 5 * 60 * 1000,
  })
}

export function useUpdateColumnWidth(planId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ columnKey, width }: { columnKey: string; width: number }) =>
      upsertColumnWidth(planId, columnKey, width),
    // Optimistic: write the cache before the request so the grid's
    // "apply saved widths" effect never sees the stale value (no snap-back).
    onMutate: async ({ columnKey, width }) => {
      const key = queryKeys.columnWidths.byPlan(planId)
      const previous = queryClient.getQueryData<Record<string, number>>(key)
      queryClient.setQueryData<Record<string, number>>(key, prev => ({
        ...prev,
        [columnKey]: width,
      }))
      await queryClient.cancelQueries({ queryKey: key })
      return { previous }
    },
    onError: (error: Error, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          queryKeys.columnWidths.byPlan(planId),
          context.previous
        )
      }
      toast.error(getErrorMessage(error, 'Failed to save column width'))
    },
  })
}
